/**
 * A short audible chime for high-signal notifications (a chat @mention —
 * "someone is trying to reach you"). Synthesised with the Web Audio API so we
 * ship no audio asset (no CORS / desktop-CSP concern) and the sound is the same
 * on web and in the Tauri webview.
 *
 * Best-effort by design: returns `false` (never throws) when Web Audio is
 * unavailable or the browser blocks playback before a user gesture (autoplay
 * policy). Staff have invariably interacted with the app before a mention
 * arrives, so in practice the AudioContext is unlocked. The AudioContext
 * factory is injectable so the logic is unit-testable without real audio.
 */

/** The slice of the Web Audio API this chime uses (kept minimal so a fake is
 * trivial to inject in tests). */
export interface AudioContextLike {
  readonly currentTime: number;
  readonly destination: unknown;
  createOscillator(): {
    type: string;
    frequency: { setValueAtTime(value: number, time: number): void };
    connect(node: unknown): void;
    start(time?: number): void;
    stop(time?: number): void;
  };
  createGain(): {
    gain: {
      setValueAtTime(value: number, time: number): void;
      exponentialRampToValueAtTime(value: number, time: number): void;
    };
    connect(node: unknown): void;
  };
}

function defaultAudioContext(): AudioContextLike | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    AudioContext?: new () => AudioContextLike;
    webkitAudioContext?: new () => AudioContextLike;
  };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  return Ctor ? new Ctor() : null;
}

/**
 * Play the notification chime. Returns whether a sound was started.
 * @param makeCtx injectable AudioContext factory (defaults to the browser's).
 */
export function playNotificationSound(
  makeCtx: () => AudioContextLike | null = defaultAudioContext,
): boolean {
  let ctx: AudioContextLike | null;
  try {
    ctx = makeCtx();
  } catch {
    return false;
  }
  if (!ctx) return false;
  try {
    const t0 = ctx.currentTime;
    tone(ctx, 880, t0, 0.12); // first ping
    tone(ctx, 1320, t0 + 0.12, 0.18); // brighter second ping
    return true;
  } catch {
    return false;
  }
}

/** One enveloped sine tone (quick attack, exponential decay) so the chime is
 * pleasant rather than a click. */
function tone(ctx: AudioContextLike, freq: number, start: number, dur: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.2, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}
